// ── GameTable — V2 two-row moneyline table ──────────────────────────
// Per-team Yes/No price pills, global Yes/No toggle in the heading.
// Selecting a team row expands it inline into a 3-level bid/ask ladder
// for whichever side the global toggle has selected. The other team's
// row stays as the standard summary row. The right-side detail panel
// keeps the deeper streaming view; this is the quick view.

import { Fragment, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import { Th, Td } from '@/components/atoms/TableCells';
import { SortableTh, type SortDirection } from '@/components/atoms/SortableTh';
import { formatCompactCurrency } from '@/lib/infra/formatters';
import { roundLabel } from '@/services/hydraAnalytics';
import type {
  UserPositionSlice,
  UserOrderSlice,
} from '@/hooks/useUserKalshiPOS';
import type { GameRow, MarketType, MarketSide, PriceMode, TeamSide, BookLevel, VenueBook } from './types';
import { americanOdds, bestPriceCentsForOrderSide } from './tradeModalBook';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeeFor } from '@/services/hydraFees';
import { perContractFeeCents } from '@/lib/pricing/fees';

// ── Props ────────────────────────────────────────────────────────────

interface GameTableProps {
  games: GameRow[];
  seriesTicker: string;
  marketType: MarketType;
  marketSide: MarketSide;
  priceMode: PriceMode;
  expandedGameKey: string | null;
  expandedTeamSide: TeamSide | null;
  onToggleExpand: (key: string, side: TeamSide) => void;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
  ) => void;
  /** Per-Kalshi-ticker POS overlay for the logged-in user. Empty Maps until
   *  the first /api/trader/positions + /api/kalshi/orders responses arrive. */
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatVolume(vol: number): string {
  if (vol === 0) return '—';
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`;
  const rounded = Math.round(vol);
  if (rounded >= 1_000) return rounded.toLocaleString();
  return String(rounded);
}

const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  moneyline: 'Moneylines',
  spread: 'Spreads',
  total: 'Totals',
  series: 'Series',
};

const LADDER_LEVELS = 3;
// Slack around the last trade for sanity-filtering orderbook levels. Kalshi
// occasionally sends bid levels well above the prevailing ask (and vice
// versa) — usually tiny stub orders or stale ones that its own UI hides.
// Levels more than this many cents away from the last trade on their side
// are treated as phantoms.
const LEVEL_SANITY_SLACK = 15;

// Levels with sub-contract size are always phantoms.
function hasSize(l: BookLevel): boolean {
  return l.size >= 1;
}

// Filter for legitimate bid levels on whichever side `lastTradeCents`
// represents. A bid is "real" if it's at or below the last trade plus a
// modest slack; bids materially above the last trade cross the book and
// are almost always stale or stub orders that Kalshi itself hides.
// Returns a permissive predicate when no last trade is known.
function bidIsReal(lastTradeCents: number | null | undefined) {
  return (l: BookLevel): boolean => {
    if (!hasSize(l)) return false;
    if (lastTradeCents == null) return true;
    return l.priceCents <= lastTradeCents + LEVEL_SANITY_SLACK;
  };
}

// Best ask price for a given market side, derived from the opposite side's bids.
// YES ask = 100 − best NO bid; NO ask = 100 − best YES bid.
function bestAskCentsForBook(
  venueBook: VenueBook | null | undefined,
  marketSide: MarketSide,
): number | null {
  if (!venueBook) return null;
  const opposite = marketSide === 'yes' ? venueBook.no : venueBook.yes;
  // Anchor: opposite bids' "last trade" reference is 100 − (this side's last YES trade).
  // For YES side, opposite is NO bids → reference is NO last = 100 − lastYes.
  // For NO  side, opposite is YES bids → reference is YES last = lastYes.
  const lastYes = venueBook.lastPriceCents;
  const oppositeLast =
    lastYes == null ? null : marketSide === 'yes' ? 100 - lastYes : lastYes;
  const bestOppositeBid = opposite.find(bidIsReal(oppositeLast))?.priceCents;
  return bestOppositeBid != null ? 100 - bestOppositeBid : null;
}

/** Pill price for a (book, side) under the toolbar's PriceMode.
 *  - maker  → highest standing bid on this side's ladder
 *  - taker  → lowest ask on this side (= 100 − best opposite bid)
 *  - market → last trade price (book.lastPriceCents is YES; invert for NO)
 *  Returns `null` when the chosen mode has no data — the pill renders "—". */
function pillPriceCents(
  book: VenueBook | null | undefined,
  side: MarketSide,
  mode: PriceMode,
): number | null {
  if (!book) return null;
  if (mode === 'maker') {
    return bestPriceCentsForOrderSide(book, side) ?? null;
  }
  if (mode === 'taker') {
    return bestAskCentsForBook(book, side);
  }
  const lastYes = book.lastPriceCents;
  if (lastYes == null) return null;
  return side === 'yes' ? lastYes : 100 - lastYes;
}

// ── GameTable ────────────────────────────────────────────────────────

export function GameTable({
  games,
  seriesTicker,
  marketType,
  marketSide,
  priceMode,
  expandedGameKey,
  expandedTeamSide,
  onToggleExpand,
  onTradeClick,
  positionByTicker,
  ordersByTicker,
}: GameTableProps) {
  const [sortKey, setSortKey] = useState<string | null>('time');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="bg-card border-border rounded-lg border">
      {/* Title bar — sport + market type. YES/NO + price-mode toggles live on
          the toolbar's row 2 above. */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">
            {games[0]?.sport?.toUpperCase() ?? ''} {MARKET_TYPE_LABELS[marketType]}
          </h2>
          <span className="text-muted-foreground text-xs">&mdash;</span>
          <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <SortableTh className="w-24" sortKey="date" activeSortKey={sortKey} activeSortDir={sortDir} onSort={handleSort}>
                Date
              </SortableTh>
              <SortableTh className="w-48" sortKey="team" activeSortKey={sortKey} activeSortDir={sortDir} onSort={handleSort}>
                Team
              </SortableTh>
              <Th className="w-32 text-center">Kalshi</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">K Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">K Liq</Th>
              <Th className="w-32 text-center">Poly</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">P Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">P Liq</Th>
            </tr>
          </thead>
          <tbody>
            {games.map((game, gameIndex) => (
              <GameRows
                key={game.key}
                game={game}
                gameIndex={gameIndex}
                marketSide={marketSide}
                priceMode={priceMode}
                expandedTeamSide={expandedGameKey === game.key ? expandedTeamSide : null}
                onToggleExpand={onToggleExpand}
                onTradeClick={onTradeClick}
                positionByTicker={positionByTicker}
                ordersByTicker={ordersByTicker}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {games.length === 0 && (
        <div className="text-muted-foreground p-8 text-center">No games available</div>
      )}
    </div>
  );
}

// ── GameRows — team rows + optional ladder for the expanded team ─────

interface GameRowsProps {
  game: GameRow;
  gameIndex: number;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** null when no team in this game is expanded */
  expandedTeamSide: TeamSide | null;
  onToggleExpand: (key: string, side: TeamSide) => void;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
  ) => void;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}

function GameRows({
  game,
  gameIndex,
  marketSide,
  priceMode,
  expandedTeamSide,
  onToggleExpand,
  onTradeClick,
  positionByTicker,
  ordersByTicker,
}: GameRowsProps) {

  const renderTeamRow = (teamSide: TeamSide, isFirstRow: boolean) => {
    const teamName = teamSide === 'away' ? game.awayName : game.homeName;
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;

    // Pill prices — what number each Y/N pill displays. Driven by the toolbar
    // PriceMode (maker = best bid, taker = best ask, market = last trade).
    const yesAskCents = pillPriceCents(kBook, 'yes', priceMode);
    const noAskCents = pillPriceCents(kBook, 'no', priceMode);
    const polyYesAskCents = pillPriceCents(pBook, 'yes', priceMode);
    const polyNoAskCents = pillPriceCents(pBook, 'no', priceMode);

    // Thick top border between games (not on first game, not when this game is expanded — ladder gets it)
    const isExpanded = expandedTeamSide !== null;
    const borderClass = isFirstRow && gameIndex > 0 && !isExpanded ? 'border-t-2 border-border' : '';

    // Zebra striping — both rows of a game share the same tint
    const zebraStyle =
      gameIndex % 2 === 1
        ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
        : undefined;

    const isThisRowExpanded = expandedTeamSide === teamSide;

    return (
      <tr
        key={`${game.key}-${teamSide}`}
        className={cn(
          'hover:bg-accent/50 cursor-pointer transition-colors',
          borderClass,
          isThisRowExpanded && 'bg-accent/30',
        )}
        style={zebraStyle}
        onClick={() => onToggleExpand(game.key, teamSide)}
      >
        {/* Date (first row only). Series-winner rows repurpose the
            column to a badge since the market spans many calendar days;
            per-day rows (including clinch rows that borrowed series
            tickers) just show the date — they're meant to look like any
            other game. */}
        <Td className="text-muted-foreground font-mono text-xs">
          {isFirstRow ? (
            game.series ? (
              <span
                className="text-foreground inline-block rounded border border-amber-500/60 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400"
                title={`Playoff series — ${roundLabel(game.sport, game.series.round)}, 20${game.series.year}`}
              >
                {roundLabel(game.sport, game.series.round)}
              </span>
            ) : (
              game.date
            )
          ) : ''}
        </Td>

        {/* Team — name plus inline per-user POS chips (contracts/open orders) */}
        <Td className="font-medium">
          <div className="flex items-center gap-2">
            {isThisRowExpanded ? (
              <span
                className={cn(
                  'inline-block rounded-md border px-2 py-0.5',
                  marketSide === 'yes'
                    ? 'border-positive bg-positive/10 text-positive'
                    : 'border-negative bg-negative/10 text-negative',
                )}
              >
                {teamName}
              </span>
            ) : (
              <span>{teamName}</span>
            )}
            <PosCell
              ticker={
                teamSide === 'away' ? game.kalshi?.away.marketTicker : game.kalshi?.home.marketTicker
              }
              book={teamSide === 'away' ? game.kalshi?.away ?? null : game.kalshi?.home ?? null}
              positionByTicker={positionByTicker}
              ordersByTicker={ordersByTicker}
            />
          </div>
        </Td>

        {/* Kalshi Yes/No pills */}
        <Td className="whitespace-nowrap text-center">
          <div className="flex items-center justify-center gap-1.5">
            <PricePill
              label="Y"
              cents={yesAskCents}
              side="yes"
              activeMarketSide={marketSide}
              disabled={kBook == null}
              ticker={kBook?.marketTicker ?? null}
              onClick={(e) => {
                e.stopPropagation();
                if (yesAskCents == null) return;
                onTradeClick?.(game.key, teamSide, 'kalshi', {
                  priceCents: yesAskCents,
                  initialOrderSide: 'yes',
                  postOnly: false,
                });
              }}
            />
            <PricePill
              label="N"
              cents={noAskCents}
              side="no"
              activeMarketSide={marketSide}
              disabled={kBook == null}
              ticker={kBook?.marketTicker ?? null}
              onClick={(e) => {
                e.stopPropagation();
                if (noAskCents == null) return;
                onTradeClick?.(game.key, teamSide, 'kalshi', {
                  priceCents: noAskCents,
                  initialOrderSide: 'no',
                  postOnly: false,
                });
              }}
            />
          </div>
        </Td>

        {/* K Volume */}
        <Td className="text-muted-foreground text-right font-mono text-xs">
          <span key={kBook?.volume} className="animate-flash-neutral inline-block">
            {kBook ? formatVolume(kBook.volume) : '—'}
          </span>
        </Td>

        {/* K Liquidity — total $ in book; ladder pills scale against this */}
        <Td className="text-muted-foreground text-right font-mono text-xs">
          {kBook ? formatCompactCurrency(kBook.liquidity) : '—'}
        </Td>

        {/* Polymarket Yes/No pills — same pattern as Kalshi */}
        <Td className="whitespace-nowrap text-center">
          <div className="flex items-center justify-center gap-1.5">
            <PricePill
              label="Y"
              cents={polyYesAskCents}
              side="yes"
              activeMarketSide={marketSide}
              disabled={pBook == null}
              ticker={pBook?.marketTicker ?? null}
              onClick={(e) => {
                e.stopPropagation();
                onTradeClick?.(game.key, teamSide, 'polymarket', {
                  priceCents: polyYesAskCents ?? undefined,
                  initialOrderSide: 'yes',
                  postOnly: false,
                });
              }}
            />
            <PricePill
              label="N"
              cents={polyNoAskCents}
              side="no"
              activeMarketSide={marketSide}
              disabled={pBook == null}
              ticker={pBook?.marketTicker ?? null}
              onClick={(e) => {
                e.stopPropagation();
                onTradeClick?.(game.key, teamSide, 'polymarket', {
                  priceCents: polyNoAskCents ?? undefined,
                  initialOrderSide: 'no',
                  postOnly: false,
                });
              }}
            />
          </div>
        </Td>

        {/* P Volume */}
        <Td className="text-muted-foreground text-right font-mono text-xs">
          <span key={pBook?.volume} className="animate-flash-neutral inline-block">
            {pBook ? formatVolume(pBook.volume) : '—'}
          </span>
        </Td>

        {/* P Liquidity — total $ in book; ladder pills scale against this */}
        <Td className="text-muted-foreground text-right font-mono text-xs">
          {pBook ? formatCompactCurrency(pBook.liquidity) : '—'}
        </Td>
      </tr>
    );
  };

  // 3 ask rows + last-trade strip + 3 bid rows for the expanded team's Kalshi
  // and Polymarket books in parallel columns.
  // YES side: asks come from inverting NO bids (100 − no[i].price), bids come from yes[].
  // NO  side: asks come from inverting YES bids, bids come from no[].
  const renderLadderRows = (teamSide: TeamSide) => {
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;
    if (!kBook && !pBook) return null;

    const buildSide = (book: VenueBook | null | undefined) => {
      if (!book) return { asks: [] as BookLevel[], bids: [] as BookLevel[] };
      const ownBids = marketSide === 'yes' ? book.yes : book.no;
      const oppositeBids = marketSide === 'yes' ? book.no : book.yes;
      const lastYes = book.lastPriceCents;
      const ownLast = lastYes == null ? null : marketSide === 'yes' ? lastYes : 100 - lastYes;
      const oppositeLast = lastYes == null ? null : marketSide === 'yes' ? 100 - lastYes : lastYes;
      return {
        asks: oppositeBids
          .filter(bidIsReal(oppositeLast))
          .slice(0, LADDER_LEVELS)
          .map((l) => ({ priceCents: 100 - l.priceCents, size: l.size })),
        bids: ownBids.filter(bidIsReal(ownLast)).slice(0, LADDER_LEVELS),
      };
    };

    const k = buildSide(kBook);
    const p = buildSide(pBook);

    // Scale anchor for ladder liquidity pills: % of total venue book liquidity.
    const kMaxLiq = kBook?.liquidity && kBook.liquidity > 0 ? kBook.liquidity : 1;
    const pMaxLiq = pBook?.liquidity && pBook.liquidity > 0 ? pBook.liquidity : 1;

    // Per-price-level user-order chips for the Kalshi bid rows. Filtered to
    // marketSide (the side currently expanded) and sorted within each price
    // level by qty desc so the largest order shows first.
    const kUserOrdersByCents = (() => {
      const map = new Map<number, BidRowOrder[]>();
      if (!kBook) return map;
      const orders = ordersByTicker.get(kBook.marketTicker) ?? [];
      for (const o of orders) {
        if (o.side !== marketSide) continue;
        const depth = queueDepth(o, kBook);
        const list = map.get(o.priceCents) ?? [];
        list.push({
          orderId: o.orderId,
          remainingCount: o.remainingCount,
          queueOrBetter: depth.atLevelOrBetter,
          tooltip: orderTooltip(o, kBook),
        });
        map.set(o.priceCents, list);
      }
      for (const list of map.values()) {
        list.sort((a, b) => b.remainingCount - a.remainingCount);
      }
      return map;
    })();

    // Visual order: asks highest→lowest (worst at top, best at bottom near strip), then strip,
    // then bids highest→lowest (best at top near strip, worst at bottom).
    const kAsksVisual = [...k.asks].reverse();
    const kBidsVisual = k.bids;
    const pAsksVisual = [...p.asks].reverse();
    const pBidsVisual = p.bids;

    // hydra reports last trade as the YES last; invert for NO side.
    // Render a separate strip price per venue so K and P can diverge.
    const lastFor = (book: VenueBook | null | undefined): number | null => {
      const lastYes = book?.lastPriceCents ?? null;
      if (lastYes == null) return null;
      return marketSide === 'yes' ? lastYes : 100 - lastYes;
    };
    const kLast = lastFor(kBook);
    const pLast = lastFor(pBook);
    const sideLabel = marketSide === 'yes' ? 'Yes' : 'No';
    const sideColorClass = marketSide === 'yes' ? 'text-positive' : 'text-negative';

    const padTo = (rows: BookLevel[]): (BookLevel | null)[] => {
      const out: (BookLevel | null)[] = [...rows];
      while (out.length < LADDER_LEVELS) out.push(null);
      return out;
    };

    const kAsksPadded = padTo(kAsksVisual);
    const kBidsPadded = padTo(kBidsVisual);
    const pAsksPadded = padTo(pAsksVisual);
    const pBidsPadded = padTo(pBidsVisual);

    return (
      <>
        {/* Ask rows */}
        {kAsksPadded.map((kLvl, idx) => (
          <LadderRow
            key={`${game.key}-${teamSide}-ask-${idx}`}
            kLevel={kLvl}
            pLevel={pAsksPadded[idx] ?? null}
            kTicker={kBook?.marketTicker ?? null}
            pTicker={pBook?.marketTicker ?? null}
            kMaxLiq={kMaxLiq}
            pMaxLiq={pMaxLiq}
            kind="ask"
            onKalshiClick={() => {
              if (!kLvl) return;
              onTradeClick?.(game.key, teamSide, 'kalshi', {
                priceCents: kLvl.priceCents,
                initialOrderSide: marketSide,
                postOnly: false,
              });
            }}
            onPolyClick={() => {
              const pLvl = pAsksPadded[idx];
              if (!pLvl) return;
              onTradeClick?.(game.key, teamSide, 'polymarket', {
                priceCents: pLvl.priceCents,
                initialOrderSide: marketSide,
                postOnly: false,
              });
            }}
          />
        ))}

        {/* Last-trade strip — separate per venue so K and P can show their own last */}
        <tr className="border-y border-border/60 bg-muted/30">
          {/* Spacer over Date / Team */}
          <Td colSpan={2} className="py-1"> </Td>

          {/* Kalshi strip (Kalshi pills + K Vol + K Liq columns) */}
          <Td colSpan={3} className="py-1 text-center text-[11px] font-semibold">
            {kBook ? (
              <>
                <span className={sideColorClass}>Trade {sideLabel}</span>
                <span className="text-muted-foreground ml-2">
                  Last {kLast != null ? `${kLast}¢` : '—'}
                </span>
              </>
            ) : null}
          </Td>

          {/* Poly strip (Poly + P Vol + P Liq columns) */}
          <Td colSpan={3} className="py-1 text-center text-[11px] font-semibold">
            {pBook ? (
              <>
                <span className={sideColorClass}>Trade {sideLabel}</span>
                <span className="text-muted-foreground ml-2">
                  Last {pLast != null ? `${pLast}¢` : '—'}
                </span>
              </>
            ) : null}
          </Td>
        </tr>

        {/* Bid rows */}
        {kBidsPadded.map((kLvl, idx) => (
          <LadderRow
            key={`${game.key}-${teamSide}-bid-${idx}`}
            kLevel={kLvl}
            pLevel={pBidsPadded[idx] ?? null}
            kTicker={kBook?.marketTicker ?? null}
            pTicker={pBook?.marketTicker ?? null}
            kMaxLiq={kMaxLiq}
            pMaxLiq={pMaxLiq}
            kind="bid"
            kUserOrders={kLvl ? kUserOrdersByCents.get(kLvl.priceCents) : undefined}
            onKalshiClick={() => {
              if (!kLvl) return;
              onTradeClick?.(game.key, teamSide, 'kalshi', {
                priceCents: kLvl.priceCents,
                initialOrderSide: marketSide,
                postOnly: true,
              });
            }}
            onPolyClick={() => {
              const pLvl = pBidsPadded[idx];
              if (!pLvl) return;
              onTradeClick?.(game.key, teamSide, 'polymarket', {
                priceCents: pLvl.priceCents,
                initialOrderSide: marketSide,
                postOnly: true,
              });
            }}
          />
        ))}
      </>
    );
  };

  return (
    <Fragment>
      {renderTeamRow('away', true)}
      {expandedTeamSide === 'away' && renderLadderRows('away')}
      {renderTeamRow('home', false)}
      {expandedTeamSide === 'home' && renderLadderRows('home')}
    </Fragment>
  );
}

// ── PosCell — per-user Kalshi position + open-order chips ───────────
// Renders side-by-side chips for the row's Kalshi ticker, scoped to the
// logged-in user. The position chip ('c') uses an SVG ring split into a
// green YES arc and a red NO arc proportional to the user's net contracts
// per side, so a 75/25 holding shows 75% green + 25% red. Each open order
// gets its own ('o') chip with `queue_position / queue_position + better`
// — "better" means cumulative same-side bid depth at strictly more
// aggressive prices, recomputed from the current orderbook on every render
// (no extra subscription; the row already updates with marketStore RAF).

function PosRing({
  yesCount,
  noCount,
  letter,
  letterColorClass = 'fill-sky-400',
  size = 14,
}: {
  yesCount: number;
  noCount: number;
  letter: string;
  /** Tailwind `fill-*` class for the letter glyph. Defaults to Kalshi blue. */
  letterColorClass?: string;
  /** Pixel diameter of the ring. Geometry scales proportionally. */
  size?: number;
}) {
  const total = yesCount + noCount;
  if (total <= 0) return null;
  // Hold geometry in the viewBox = 14 (the ring's design size); render at
  // `size` px so the SVG scales but proportions stay identical.
  const r = 6;
  const cx = 7;
  const cy = 7;
  const circumference = 2 * Math.PI * r;
  const yesFraction = yesCount / total;
  const yesLen = circumference * yesFraction;
  const noLen = circumference - yesLen;
  // Start strokes at 12 o'clock and sweep clockwise.
  const rotateForStart = -90;
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" className="shrink-0">
      <g transform={`rotate(${rotateForStart} ${cx} ${cy})`}>
        {/* Green YES arc */}
        {yesLen > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            strokeWidth={1.5}
            className="stroke-positive"
            strokeDasharray={`${yesLen} ${circumference}`}
            strokeDashoffset={0}
          />
        )}
        {/* Red NO arc, offset to start where YES ends */}
        {noLen > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            strokeWidth={1.5}
            className="stroke-negative"
            strokeDasharray={`${noLen} ${circumference}`}
            strokeDashoffset={-yesLen}
          />
        )}
      </g>
      <text
        x={cx}
        y={cy + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        className={cn('font-semibold', letterColorClass)}
        style={{ fontSize: 7 }}
      >
        {letter}
      </text>
    </svg>
  );
}

/** Same wording as the inline O-ring tooltip in PosCell — kept as a single
 *  helper so the bid-row chips on the inline ladder match exactly. */
function orderTooltip(
  order: { action: 'buy' | 'sell'; side: 'yes' | 'no'; priceCents: number; remainingCount: number; queuePosition: number | null },
  book: VenueBook | null,
): string {
  const depth = queueDepth(order, book);
  const totalRounded = depth.atLevelOrBetter != null ? Math.round(depth.atLevelOrBetter) : null;
  const levelRounded = depth.atLevel != null ? Math.round(depth.atLevel) : null;
  const detail =
    totalRounded != null
      ? `${totalRounded.toLocaleString()} ahead total${
          levelRounded != null ? `, ${levelRounded.toLocaleString()} at this level` : ''
        }`
      : levelRounded != null
        ? `${levelRounded.toLocaleString()} ahead at this level`
        : 'queue position pending';
  return `${order.action.toUpperCase()} ${order.side.toUpperCase()} ${order.remainingCount} @ ${order.priceCents}¢ — ${detail}`;
}

interface BidRowOrder {
  orderId: string;
  remainingCount: number;
  /** Total contracts queued ahead at this price + better prices (matches the
   *  "1.6M ahead total" figure the inline O-ring tooltip already shows). */
  queueOrBetter: number | null;
  tooltip: string;
}

function queueDepth(
  order: { side: 'yes' | 'no'; priceCents: number; queuePosition: number | null },
  book: VenueBook | null,
): { atLevel: number | null; atLevelOrBetter: number | null } {
  if (order.queuePosition == null) return { atLevel: null, atLevelOrBetter: null };
  if (!book) return { atLevel: order.queuePosition, atLevelOrBetter: null };
  // Same-side bids strictly above the order's price = "better" levels
  // (priced more aggressively, fill first). Sum their resting size.
  const sameSide = order.side === 'yes' ? book.yes : book.no;
  let better = 0;
  for (const l of sameSide) {
    if (l.priceCents > order.priceCents) better += l.size;
  }
  return {
    atLevel: order.queuePosition,
    atLevelOrBetter: order.queuePosition + better,
  };
}

function PosCell({
  ticker,
  book,
  positionByTicker,
  ordersByTicker,
}: {
  ticker: string | null | undefined;
  book: VenueBook | null;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}) {
  if (!ticker) return null;
  const pos = positionByTicker.get(ticker);
  const orders = ordersByTicker.get(ticker);
  if (!pos && (!orders || orders.length === 0)) return null;

  return (
    <div className="flex items-center gap-1">
      {pos && pos.yesCount + pos.noCount > 0 && (
        <span
          className="inline-flex items-center"
          title={
            pos.yesCount > 0 && pos.noCount > 0
              ? `${pos.yesCount} YES, ${pos.noCount} NO`
              : pos.yesCount > 0
                ? `${pos.yesCount} YES contract${pos.yesCount === 1 ? '' : 's'}`
                : `${pos.noCount} NO contract${pos.noCount === 1 ? '' : 's'}`
          }
        >
          <PosRing yesCount={pos.yesCount} noCount={pos.noCount} letter="C" size={20} />
        </span>
      )}
      {/* Open-order indicator — qty/queue text now lives on the bid row in
          the inline ladder; here we just keep the O ring with hover. */}
      {orders?.map((o) => {
        const yesShare = o.side === 'yes' ? 1 : 0;
        return (
          <span
            key={o.orderId}
            className="inline-flex items-center"
            title={orderTooltip(o, book)}
          >
            <PosRing yesCount={yesShare} noCount={1 - yesShare} letter="O" size={20} />
          </span>
        );
      })}
    </div>
  );
}

// ── PricePill ────────────────────────────────────────────────────────

function PricePill({
  label,
  cents,
  side,
  activeMarketSide,
  disabled,
  onClick,
  ticker,
}: {
  label: string;
  cents: number | null;
  side: MarketSide;
  activeMarketSide: MarketSide;
  disabled: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Used to look up the per-series fee schedule when the dashboard's
   *  Fees toggle is on. Polymarket and unknown tickers fall back to raw. */
  ticker?: string | null;
}) {
  const isActive = side === activeMarketSide;
  const sideClass =
    side === 'yes'
      ? isActive
        ? 'border-positive text-foreground bg-positive/10'
        : 'border-border/60 text-muted-foreground hover:text-positive hover:border-positive/60'
      : isActive
        ? 'border-negative text-foreground bg-negative/10'
        : 'border-border/60 text-muted-foreground hover:text-negative hover:border-negative/60';
  // Default convention: maker fees (the typical bidding workflow).
  // Toggle off → raw odds. Loading / unknown series / `flat` → raw fallback.
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const feeEntry = useFeeFor(ticker);
  const makerRate =
    !feeAdjusted || !feeEntry || feeEntry.feeType === 'flat'
      ? 0
      : (feeEntry.feeMakerRate ?? 0);
  const effectiveCents =
    cents == null || makerRate === 0 ? cents : cents + perContractFeeCents(cents, makerRate);
  const odds = effectiveCents != null ? americanOdds(effectiveCents) : null;

  return (
    <button
      type="button"
      disabled={disabled || cents == null}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[13px] leading-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
        sideClass,
      )}
      title={
        cents != null
          ? `Click to trade ${label} @ ${cents}¢${odds ? ` (${odds})` : ''}`
          : undefined
      }
    >
      <span className="font-semibold">{label}</span>
      <span>{cents != null ? `${cents}¢` : '—'}</span>
      {odds && (
        <>
          <span className="h-3 w-px self-center bg-current opacity-30" />
          <span className="opacity-70">{odds}</span>
        </>
      )}
    </button>
  );
}

// ── LadderRow ────────────────────────────────────────────────────────

function LadderRow({
  kLevel,
  pLevel,
  kTicker,
  pTicker,
  kMaxLiq,
  pMaxLiq,
  kind,
  kUserOrders,
  onKalshiClick,
  onPolyClick,
}: {
  kLevel: BookLevel | null;
  pLevel: BookLevel | null;
  kTicker?: string | null;
  pTicker?: string | null;
  kMaxLiq: number;
  pMaxLiq: number;
  kind: 'ask' | 'bid';
  /** User open orders matching this Kalshi bid row's price (bid kind only).
   *  Pre-sorted by qty desc, with same hover tooltip as the inline O ring. */
  kUserOrders?: BidRowOrder[];
  onKalshiClick: () => void;
  onPolyClick: () => void;
}) {
  const kLiq = kLevel ? Math.round((kLevel.priceCents * kLevel.size) / 100) : 0;
  const pLiq = pLevel ? Math.round((pLevel.priceCents * pLevel.size) / 100) : 0;
  const colorClass = kind === 'ask' ? 'text-negative' : 'text-positive';
  const hasUserOrders = kUserOrders && kUserOrders.length > 0;

  // Per-row American odds. Same Fees/Raw rule as the YES/NO pills above:
  // toggle on → maker-rate-adjusted; toggle off → raw price odds.
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const kFeeEntry = useFeeFor(kTicker);
  const pFeeEntry = useFeeFor(pTicker);
  const kMakerRate =
    !feeAdjusted || !kFeeEntry || kFeeEntry.feeType === 'flat'
      ? 0
      : (kFeeEntry.feeMakerRate ?? 0);
  const pMakerRate =
    !feeAdjusted || !pFeeEntry || pFeeEntry.feeType === 'flat'
      ? 0
      : (pFeeEntry.feeMakerRate ?? 0);
  const kEffectiveCents = kLevel
    ? kMakerRate === 0
      ? kLevel.priceCents
      : kLevel.priceCents + perContractFeeCents(kLevel.priceCents, kMakerRate)
    : null;
  const pEffectiveCents = pLevel
    ? pMakerRate === 0
      ? pLevel.priceCents
      : pLevel.priceCents + perContractFeeCents(pLevel.priceCents, pMakerRate)
    : null;
  const kOdds = kEffectiveCents != null ? americanOdds(kEffectiveCents) : null;
  const pOdds = pEffectiveCents != null ? americanOdds(pEffectiveCents) : null;

  return (
    <tr className="border-0">
      <Td className="py-0.5"> </Td>
      <Td className="text-muted-foreground/70 py-0.5 text-right text-[10px] uppercase">
        {kLevel || pLevel ? (kind === 'ask' ? 'Ask' : 'Bid') : ''}
      </Td>

      {/* Kalshi price + American odds — bid rows render any user-order chips
          (qty + queue position, vertical-bar separated, qty desc) to the
          LEFT of the price+odds. Chips are inert (cursor + click both stop
          at them); only the price itself opens the trade modal. Same chip
          structure will apply to the Poly price column when Poly user
          orders are wired in. */}
      <Td className={cn('whitespace-nowrap py-0.5 font-mono', !hasUserOrders && 'text-right')}>
        {hasUserOrders ? (
          <div className="flex items-center justify-end gap-1.5">
            <div
              className="text-foreground/90 flex min-w-0 flex-1 cursor-default items-center gap-1.5 overflow-hidden text-left text-[10px]"
              onClick={(e) => e.stopPropagation()}
            >
              {kUserOrders!.map((o, i) => (
                <Fragment key={o.orderId}>
                  {i > 0 && <span className="bg-muted-foreground/40 h-3 w-px shrink-0 self-center" />}
                  <span className="truncate" title={o.tooltip}>
                    {Math.round(o.remainingCount)}/
                    {o.queueOrBetter != null ? formatVolume(Math.round(o.queueOrBetter)) : '?'}
                  </span>
                </Fragment>
              ))}
            </div>
            {kLevel && (
              <span className="inline-flex shrink-0 items-center gap-1">
                <span
                  className={cn('cursor-pointer hover:underline', colorClass)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onKalshiClick();
                  }}
                  title="Click to trade"
                >
                  {kLevel.priceCents}¢
                </span>
                {kOdds && (
                  <>
                    <span className="text-muted-foreground/40 text-xs">|</span>
                    <span className="text-foreground text-xs">{kOdds}</span>
                  </>
                )}
              </span>
            )}
          </div>
        ) : kLevel ? (
          <span className="inline-flex items-center gap-1">
            <span
              className={cn('cursor-pointer hover:underline', colorClass)}
              onClick={(e) => {
                e.stopPropagation();
                onKalshiClick();
              }}
              title="Click to trade"
            >
              {kLevel.priceCents}¢
            </span>
            {kOdds && (
              <>
                <span className="text-muted-foreground/40 text-xs">|</span>
                <span className="text-foreground text-xs">{kOdds}</span>
              </>
            )}
          </span>
        ) : null}
      </Td>

      {/* K Size */}
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {kLevel ? formatVolume(kLevel.size) : ''}
      </Td>

      {/* K Liquidity — scaled to % of total Kalshi book liquidity */}
      <Td className="py-0.5">
        <LiquidityBar value={kLiq} maxValue={kMaxLiq} height={16} />
      </Td>

      {/* Polymarket price */}
      <Td className="whitespace-nowrap py-0.5 text-right font-mono">
        {pLevel ? (
          <span className="inline-flex items-center gap-1">
            <span
              className={cn('cursor-pointer hover:underline', colorClass)}
              onClick={(e) => {
                e.stopPropagation();
                onPolyClick();
              }}
              title="Click to trade"
            >
              {pLevel.priceCents}¢
            </span>
            {pOdds && (
              <>
                <span className="text-muted-foreground/40 text-xs">|</span>
                <span className="text-foreground text-xs">{pOdds}</span>
              </>
            )}
          </span>
        ) : (
          ''
        )}
      </Td>

      {/* P Size */}
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {pLevel ? formatVolume(pLevel.size) : ''}
      </Td>

      {/* P Liquidity — scaled to % of total Polymarket book liquidity */}
      <Td className="py-0.5">
        <LiquidityBar value={pLiq} maxValue={pMaxLiq} height={16} />
      </Td>
    </tr>
  );
}
